import Head from 'expo-router/head';
import { useState } from 'react';
import { ScrollView } from 'react-native';
import { getTokenValue, Text, XStack, YStack } from 'tamagui';

import DateSelector from '@/components/DateSelector';

/**
 * ── /components/date-selector ────────────────────────────────────────────
 *
 * DateSelector — the fixed-size day cell from the event page's date/time
 * picker (`DateTimePicker` in `app/events/[id]/index.tsx`, one cell per
 * distinct day inside a `Carousel`). Covers: the interactive selected/
 * unselected flip (live, tappable — same pattern as ps-button.tsx's demo),
 * a static side-by-side of both states to eyeball the fill/border/text
 * swap at rest, and the `hasApTickets` corner case where the "AP" chip
 * flattens the cell's bottom-left radius so the two shapes read as one
 * joined piece, under BOTH fill states.
 *
 * Takes the slot's RAW ISO `datetime` and formats internally via
 * `dateParts()` (`src/utils/dateFormatter.ts`) — this preview passes raw
 * ISO strings as fixtures, never pre-formatted day/month text, per the
 * house rule.
 * ─────────────────────────────────────────────────────────────────────────
 */

// Raw ISO datetimes — DateSelector splits these itself via `dateParts()`.
// `hasApTickets` on d2 exercises the AP-chip corner case below.
const PREVIEW_DATES = [
  { key: 'd1', datetime: '2026-07-10T19:30:00', hasApTickets: false },
  { key: 'd2', datetime: '2026-07-11T19:30:00', hasApTickets: true },
  { key: 'd3', datetime: '2026-07-12T19:30:00', hasApTickets: false },
];

export default function DateSelectorPreview() {
  // Drives the live demo below — mirrors DateTimePicker's `selectedDate`,
  // one selected cell at a time among a row of them.
  const [dateKey, setDateKey] = useState('d1');

  return (
    <>
      <Head>
        <title>DateSelector · Putting Scene</title>
      </Head>
      <ScrollView
        style={{
          flex: 1,
          backgroundColor: getTokenValue('$color.background', 'color'),
        }}
      >
        <YStack items="flex-start" gap="$lg" p="$lg">
          {/* Live — tap any cell to select it, same row the event page's date
            picker renders (minus Carousel's edge-to-edge bleed, which is
            Carousel's own concern, not this component's). Only one cell is
            selected at a time; d2 carries the AP chip throughout so its
            corner join is visible whichever cell is currently selected. */}
          <YStack gap="$xs">
            <Text fontFamily="$body" fontSize="$small" color="$textDarkMuted">
              live — tap a cell to select it (events/[id]&apos;s date row)
            </Text>
            <XStack gap="$md">
              {PREVIEW_DATES.map((d) => (
                <DateSelector
                  key={d.key}
                  datetime={d.datetime}
                  selected={d.key === dateKey}
                  onPress={() => setDateKey(d.key)}
                  hasApTickets={d.hasApTickets}
                />
              ))}
            </XStack>
          </YStack>

          {/* Static side-by-side of both states at rest — the live demo above
            only ever shows one selected cell, so this pins unselected next to
            selected to eyeball the flip directly: fill + border color + text
            color all move together, but the hairline border width is CONSTANT
            across both (see the component's docstring) so the cell never
            resizes when it's tapped. */}
          <YStack gap="$xs">
            <Text fontFamily="$body" fontSize="$small" color="$textDarkMuted">
              unselected (transparent fill, gray border, dark text) vs selected
              (dark fill, dark border, light text) — border width never changes,
              only its color
            </Text>
            <XStack gap="$md">
              <DateSelector
                datetime="2026-07-10T19:30:00"
                selected={false}
                hasApTickets={false}
              />
              <DateSelector
                datetime="2026-07-10T19:30:00"
                selected={true}
                hasApTickets={false}
              />
            </XStack>
          </YStack>

          {/* hasApTickets — the "AP" chip (brand fill, PSText.Tags) that hangs
            off the cell's bottom-left. The cell's own borderBottomLeftRadius
            drops to 0 whenever this is true, so the chip's top-left corner
            sits flush against the cell instead of leaving a rounded gap — the
            two pieces read as one shape. Shown under BOTH fill states since
            selecting the cell doesn't touch the chip (it's always brand
            fill + white text, independent of `selected`). */}
          <YStack gap="$xs">
            <Text fontFamily="$body" fontSize="$small" color="$textDarkMuted">
              hasApTickets — the AP chip flattens the cell&apos;s bottom-left
              corner so it joins flush, unselected and selected
            </Text>
            <XStack gap="$md">
              <DateSelector
                datetime="2026-07-11T19:30:00"
                selected={false}
                hasApTickets={true}
              />
              <DateSelector
                datetime="2026-07-11T19:30:00"
                selected={true}
                hasApTickets={true}
              />
            </XStack>
          </YStack>
        </YStack>
      </ScrollView>
    </>
  );
}
